import type { CSSProperties } from 'react';

import classNames from 'classnames';

import type { TTextLeaf } from '@/textLeaf';
import { getTextColor } from '@/textLeaf/utils';
import { generatePositionStyle, type PositionStyle } from '@/utils/positioning';

import { getTextAlignStyleVariables } from '../utils/typography';
import { useAnimatedBlock } from '../utils/useAnimatedBlock';

import { HEADING_DEFAULTS } from './Heading.constants';
import * as styles from './Heading.css';
import type { HeadingElementProps } from './Heading.types';

const sanitizeBaseColor = (color?: string) => {
  const trimmedColor = color?.trim();

  if (!trimmedColor) return undefined;

  // Corrupted values can include Tailwind utility fragments instead of a color.
  if (
    trimmedColor.startsWith('--') ||
    trimmedColor.includes('[') ||
    trimmedColor.includes(']') ||
    trimmedColor.includes(';') ||
    /\b(?:hover|group-hover|focus|active|visited|text|decoration):/.test(trimmedColor)
  ) {
    return undefined;
  }

  return trimmedColor;
};

const isTextLeaf = (node: HeadingElementProps['element']['content'][number]): node is TTextLeaf => {
  return 'text' in node && node.type === 'text';
};

const getBaseColor = (element: HeadingElementProps['element']) => {
  const { content } = element;

  // we want to get either the first child or the last child's
  // color attribute.
  const firstChild = content?.[0];
  const lastChild = content?.[content.length - 1];

  if (!firstChild || !lastChild) return undefined;

  const firstChildColor = isTextLeaf(firstChild) ? sanitizeBaseColor(getTextColor(firstChild)) : undefined;
  const lastChildColor = isTextLeaf(lastChild) ? sanitizeBaseColor(getTextColor(lastChild)) : undefined;

  return firstChildColor || lastChildColor;
};

// Keeping this as inherit could have unintended consequences with older templates so let's force to be transparent on heading elements
const getBackgroundColor = (backgroundColor: string) => {
  if (backgroundColor === 'inherit') return 'transparent';
  return backgroundColor;
};

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const HEADING_TAGS = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const;

const LEVEL_STYLE_MAP: Record<HeadingLevel, string> = {
  1: styles.h1Element,
  2: styles.h2Element,
  3: styles.h3Element,
  4: styles.h4Element,
  5: styles.h5Element,
  6: styles.h6Element,
};

type BaseHeadingProps = HeadingElementProps & {
  level: HeadingLevel;
};

function BaseHeading(props: BaseHeadingProps) {
  const { element, children, attributes, className, level, ...rest } = props;
  const {
    id,
    width,
    maxWidth,
    textAlign,
    mobileTextAlign,
    animation,
    padding,
    mobilePadding,
    letterSpacing,
    mobileLineHeight,
    lineHeight,
    margin,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    customAttributes,
    lineClampClass,
    textTransform,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);

  const Tag = HEADING_TAGS[level];

  return (
    <Tag
      ref={ref}
      {...rest}
      {...customAttributes}
      id={id}
      className={classNames(
        className,
        animatedClassname,
        styles.heading,
        {
          [LEVEL_STYLE_MAP[level]]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          ...generatePositionStyle(element.attrs as PositionStyle),
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? padding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </Tag>
  );
}

export function Heading1Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={1} />;
}

export function Heading2Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={2} />;
}

export function Heading3Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={3} />;
}

export function Heading4Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={4} />;
}

export function Heading5Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={5} />;
}

export function Heading6Element(props: HeadingElementProps) {
  return <BaseHeading {...props} level={6} />;
}
